import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './ui/Icon';
import type { VoiceMessage, VoiceMessageCard } from '../utils/voiceSessionStorage';

interface ConversationBubbleProps {
  message: VoiceMessage;
  dark?: boolean;
  /** When provided, shows a "go here" icon that reverts the chat to this message. */
  onRewind?: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  Financial: '#4A90E2',
  Reports: '#9013FE',
  Expense: '#27AE60',
  Navigation: '#6B7280',
};

const MessageCard: React.FC<{ card: VoiceMessageCard }> = ({ card }) => (
  <div className="gva-msg-card">
    <div className="gva-msg-card-header">
      {card.status && <span className="gva-msg-card-status">{card.status}</span>}
      <span className="gva-msg-card-title">{card.title}</span>
      {card.subtitle && <span className="gva-msg-card-sub">{card.subtitle}</span>}
      {card.amount && <span className="gva-msg-card-amount">{card.amount}</span>}
    </div>
    {card.meta && (
      <div className="gva-msg-card-meta">
        {Object.entries(card.meta).map(([k, v]) => (
          <span key={k}>{k}: {v}</span>
        ))}
      </div>
    )}
  </div>
);

const ConversationBubble: React.FC<ConversationBubbleProps> = ({ message, dark = true, onRewind }) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const rewindLabel = t('voice.rewindHere', 'Go here — revert chat to this point');

  return (
    <div className={`gva-bubble-row ${isUser ? 'gva-bubble-row-user' : 'gva-bubble-row-assistant'}${onRewind ? ' gva-bubble-row--rewindable' : ''}`}>
      {!isUser && (
        <div className="gva-avatar" aria-hidden="true">
          <span>AI</span>
        </div>
      )}
      <div className={`gva-bubble-col ${isUser ? 'gva-bubble-col-user' : 'gva-bubble-col-assistant'}`}>
        <p className="gva-bubble-label">{isUser ? 'You' : 'EZAccounting.ai'}</p>
        <div className="gva-bubble-line">
          {onRewind && isUser && (
            <button
              type="button"
              className="gva-rewind-btn"
              onClick={onRewind}
              title={rewindLabel}
              aria-label={rewindLabel}
            >
              <Icon name="rewind" size={14} />
            </button>
          )}
          <div className={`gva-bubble ${isUser ? 'gva-bubble-user' : 'gva-bubble-assistant'} ${dark ? 'gva-bubble-dark' : ''}`}>
            <p className={message.isError ? 'gva-bubble-error' : undefined}>{message.content}</p>
          </div>
          {onRewind && !isUser && (
            <button
              type="button"
              className="gva-rewind-btn"
              onClick={onRewind}
              title={rewindLabel}
              aria-label={rewindLabel}
            >
              <Icon name="rewind" size={14} />
            </button>
          )}
        </div>
        {message.category && (
          <span
            className="gva-category-tag"
            style={{ backgroundColor: CATEGORY_COLORS[message.category] || '#4A90E2' }}
          >
            {message.category}
          </span>
        )}
        {message.card && <MessageCard card={message.card} />}
        {message.timestamp && (
          <span className="gva-bubble-time">
            {new Date(message.timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default ConversationBubble;
